import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    IDLE,
    PENDING,
    EDIT,
    STORAGE_MICROSOFT,
    STORAGE_AWS,
    DANGER,
} from "const"
import { MtDriveFile } from "server/models"
import { StateType, hasAccess } from "client/utils"
import {
    PRM_ACCOUNT_SETTINGS,
    StoreType,

    DeleteCloudDriveSettings,
    SaveAccountSettings,

    updateAccountSettings,
    setSystemAccountSettings,
    addModal,
} from "client/store"
import {
    PageForm,
    Collapse,
    FormGroup,
    Label,
    FileLoader,
    Button,
    SpinnerIcon,
    Feedback,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
} from "client/components"

import { clearServerDriveConnection } from "client/socketApi"

import MicrosoftDriveSecrets from "./driveSecrets"

const Microsoft = () => {
    // important! editable settings, not "state.system.accountSettings"
    const accountSettings = useSelector((state: StoreType) => state.accountSettings.settings)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [testImage, setTestImage] = useState<MtDriveFile>(null)
    const [collapsed, setCollapsed] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [confirmState, setConfirmState] = useState<StateType>(IDLE)

    const onRemoveCredentials = () => {
        setShowConfirm(true)
    }

    const onCancelRemoveCredentials = () => {
        setShowConfirm(false)
    }

    const onConfirmRemoveCredentials = () => {
        setConfirmState(PENDING)
        DeleteCloudDriveSettings({
            accountId: userData.accountId,
            storage: STORAGE_MICROSOFT,
        }).then((removedRows) => {
            if (removedRows > 0) {
                let { defaultStorage } = accountSettings.drive
                if (defaultStorage === STORAGE_MICROSOFT) defaultStorage = STORAGE_AWS

                dispatch(updateAccountSettings("drive", {
                    microsoftDriveConnected: false,
                    defaultStorage,
                }))

                const accountSettingsCopy = { ...accountSettings }
                accountSettingsCopy.drive = { // update for saving
                    ...accountSettingsCopy.drive,
                    microsoftDriveConnected: false,
                    defaultStorage,
                }

                SaveAccountSettings(userData.accountId, accountSettingsCopy)
                .then(() => {
                    dispatch(setSystemAccountSettings(accountSettingsCopy))
                    setConfirmState(IDLE)
                    setShowConfirm(false)

                    clearServerDriveConnection(userData.accountId, STORAGE_MICROSOFT)
                })
            } else {
                setConfirmState(IDLE)
                setShowConfirm(false)
            }
        })
    }

    const { microsoftDriveConnected } = accountSettings.drive
    const editAccess = hasAccess(PRM_ACCOUNT_SETTINGS, EDIT)

    return (
        <Collapse
            show={collapsed}
            title={lang.microsoftOneDrive}
            onCollapse={() => setCollapsed(!collapsed)}
        >
            {
                !microsoftDriveConnected && (
                    <PageForm>
                        <p>{lang.microsoftOneDriveNotConnected}</p>
                        <Feedback variant={DANGER}>{lang.cloudDrivesWillBeNotAvailable}</Feedback>
                        <Button
                            variant="success"
                            disabled={!editAccess}
                            onClick={() => dispatch(addModal({
                                key: "addSecrets",
                                content: <MicrosoftDriveSecrets />,
                            }))}
                        >
                            {lang.connect}
                        </Button>
                    </PageForm>
                )
            }
            {
                microsoftDriveConnected && ([
                    <PageForm key="buttons">
                        <Row>
                            <Col xs={12}>
                                <p>{lang.microsoftOneDriveConnected}</p>
                                <Button
                                    row
                                    disabled={!editAccess}
                                    onClick={() => dispatch(addModal({
                                        key: "editSecrets",
                                        content: <MicrosoftDriveSecrets />,
                                    }))}
                                >
                                    {lang.edit}
                                </Button>
                                <Button
                                    row
                                    variant="danger"
                                    disabled={!editAccess}
                                    onClick={() => onRemoveCredentials()}
                                >
                                    {lang.remove}
                                </Button>
                            </Col>
                        </Row>
                    </PageForm>,
                    <PageForm key="testImage">
                        <Row>
                            <Col xs={12}>
                                <FormGroup>
                                    <Label>{lang.testImageToUpload}</Label>
                                    <FileLoader
                                        forceStorage={STORAGE_MICROSOFT}
                                        value={testImage}
                                        callback={(file) => setTestImage(file)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </PageForm>,
                ])
            }

            <ConfirmWrapper
                show={showConfirm}
                onClose={onCancelRemoveCredentials}
            >
                <Confirm>
                    <ConfirmTitle>{lang.microsoftOneDrive}</ConfirmTitle>
                    <ConfirmDescription>{lang.deleteCredentialsConfirm}</ConfirmDescription>

                    <ConfirmBottom>
                        <Button row onClick={onCancelRemoveCredentials}>{lang.cancel}</Button>
                        <Button variant="success" row onClick={onConfirmRemoveCredentials}>
                            {confirmState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </Collapse>
    )
}
export default Microsoft
